import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import { type Dispatch, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { dmsState } from '@/app/store'
import { type IUpdateContractDto, type IAddContractDto, type IConpanyContract } from '@/app/types/dmsTypes'
import { onCheckDisabledConditions } from '@/app/utils'
import { routes } from '@Config/config'

import { type TLoaderType } from '../../../helper'
import { DateField } from '../../DateField/DateField'
import { RegularField } from '../../RegularField/RegularField'
import Vector from '../../Vector.svg'

import { DocLoaderModal } from './DocLoaderModal/DocLoaderModal'
import style from './Item.module.scss'
import { TransferNotification } from './TransferNotification/TransferNotification'
import { useFields } from './useFields'

type TItemMode = 'add' | 'change'

interface IProps extends IConpanyContract {
    mode: TItemMode
    id: string
    insuranceCompanyId: string
    transferableContractError: string | null
    // transferableContract: IConpanyContract | null
    isAllowedTransfer: boolean
    onGetLastErrors: () => void
    onZeroNewContract?: () => void
    // setTransferableContract: Dispatch<React.SetStateAction<IConpanyContract | null>>
    setTransferableContractError: Dispatch<React.SetStateAction<string | null>>
    onRejectTransferableContractError: () => void
    onGetImportHistory: () => void
    onTransferPoliciesHandler: (isIgnor: boolean) => void
}

export const Item = observer((props: IProps) => {
    const {
        mode,
        insuranceCompanyId,
        transferableContractError,
        isAllowedTransfer,
        onGetLastErrors,
        setTransferableContractError,
        onZeroNewContract,
        onRejectTransferableContractError,
        onGetImportHistory,
        onTransferPoliciesHandler,
        ...rest
    } = props

    const navi = useNavigate()

    const [isEdit, setIsEdit] = useState<boolean>(false)
    const [loader, setLoader] = useState<TLoaderType | null>(null)
    const [errors, setErrors] = useState<string[]>([])

    const onCloseLoader = () => {
        setErrors([])
        setLoader(null)
    }

    const onError = (errors: string[]) => {
        setErrors(errors)
    }

    const onUploadFileCallbackHandler = (file: File, isAddedAttach: boolean) => {
        dmsState.onAddImportTemplate(rest.id, loader === 'importList', isAddedAttach, file)
    }

    const {
        name,
        onChangeName,
        onSetIsTouchedName,
        conclusionAt,
        onChangeConclusionAt,
        onSetIsTouchedConclusionAt,
        finishedAt,
        onChangeFinishedAt,
        onSetIsTouchedFinishedAt,
        updateAt,
        isSomeToched,
        onSetTouchAll
    } = useFields({ data: { ...rest } })

    const additionDto: IAddContractDto = {
        name: name.value || '',
        conclusionAt: moment(conclusionAt.value).format('YYYY-MM-DDT00:00:00.000'),
        finishedAt: moment(finishedAt.value).format('YYYY-MM-DDT23:59:59.000'),
        insuranceCompanyId
    }

    const upgateDto: IUpdateContractDto = {
        name: name.value || '',
        conclusionAt: moment(conclusionAt.value).format('YYYY-MM-DDT00:00:00.000'),
        finishedAt: moment(finishedAt.value).format('YYYY-MM-DDT23:59:59.000'),
        id: rest.id
    }

    const onGetCompanyPrograms = (contractId: string) => {
        if (insuranceCompanyId) {
            navi(`${routes.DMSM}/companies/${insuranceCompanyId}/contracts/${contractId}/programs`)
        }
    }

    const onGetPrograms = () => {
        onGetCompanyPrograms(rest.id)
    }

    const onAddContractHandler = () => {
        if (!isValidFields || isSomeToched) {
            dmsState.onAddContract?.(additionDto, onZeroNewContract)
        } else {
            onSetTouchAll()
        }
    }

    const onSaveContractChanges = () => {
        if (isEdit) {
            // setIsUpdatebleContract(true)
            dmsState.onUpdateContract(upgateDto, () => {
                setIsEdit(false)
                // setIsUpdatebleContract(false)
            })
        } else {
            setIsEdit(true)
        }
    }

    const onRemoveContractHandler = () => {
        if (isEdit) {
            setIsEdit(false)
        } else {
            dmsState.onRemoveContract?.(rest.id, insuranceCompanyId)
            // setIsEdit(true)
        }
    }

    const onSetDisabledMode = (): boolean => {
        if (!isSomeToched) {
            return false
        }
        if (!isValidFields) {
            return false
        }
        return true
    }

    const isValidFields = onCheckDisabledConditions(Boolean(!name.error), Boolean(!conclusionAt.error))

    const actions = [
        {
            title: 'Перенести полисы в договор',
            type: 'primary',
            disabled: !!transferableContractError,
            callback: () => {
                dmsState.onSetTransferableContractTargetIds({
                    oldСontractId: dmsState.transferableContractTargetIds.oldСontractId,
                    newСontractId: rest.id
                })
                onTransferPoliciesHandler(false)
            },
            belongTo: 'change',
            btnHint: 'В данный договор будут перенесены действующие полисы из выбранного договора.',
            isVisible:
                !!dmsState.transferableContractTargetIds.oldСontractId &&
                dmsState.transferableContractTargetIds.oldСontractId !== rest.id
        },
        {
            title: 'Перенос полисов',
            type: 'primary',
            disabled: !isAllowedTransfer,
            callback: () => {
                dmsState.onSetTransferableContractTargetIds({
                    newСontractId: dmsState.transferableContractTargetIds.newСontractId,
                    oldСontractId: rest.id
                })
            },
            belongTo: 'change',
            btnHint:
                'Перенос действующих полисов в другой договор, который надо выбрать и нажать на "Перенести полисы в договор"',
            isVisible:
                !dmsState.transferableContractTargetIds?.oldСontractId ||
                dmsState.transferableContractTargetIds.oldСontractId === rest.id
        },
        {
            title: 'Импорт списка',
            type: 'primary',
            disabled: false,
            callback: () => {
                onGetImportHistory()
                setLoader('importList')
            },
            belongTo: 'change',
            btnHint: 'Импортировать список застрахованных через файл с расширением ".xlsx"',
            isVisible:
                !dmsState.transferableContractTargetIds?.oldСontractId ||
                dmsState.transferableContractTargetIds.oldСontractId === rest.id
        },
        {
            title: 'Список на открепление',
            type: 'primary',
            disabled: false,
            callback: () => {
                onGetImportHistory()
                setLoader('detachPatients')
            },
            btnHint: 'Открепить список застрахованных через файл с расширением ".xlsx"',
            belongTo: 'change',
            isVisible:
                !dmsState.transferableContractTargetIds?.oldСontractId ||
                dmsState.transferableContractTargetIds.oldСontractId === rest.id
        },
        {
            title: 'Программы',
            type: 'primary',
            disabled: false,
            callback: onGetPrograms,
            belongTo: 'change',
            btnHint: 'Перейти в список программ, прикрепленных к этому договору',
            isVisible:
                !dmsState.transferableContractTargetIds?.oldСontractId ||
                dmsState.transferableContractTargetIds.oldСontractId === rest.id
        },
        {
            title: isEdit ? 'Сохранить изменения' : 'Редактировать',
            type: 'primary',
            disabled: false,
            callback: onSaveContractChanges,
            belongTo: 'change',
            btnHint: isEdit
                ? 'Сохранить изменения с обновленными данными'
                : 'Перейти в режим "редактирование" основных данных договора',
            isVisible:
                !dmsState.transferableContractTargetIds?.oldСontractId ||
                dmsState.transferableContractTargetIds.oldСontractId === rest.id
        },
        {
            title: isEdit ? 'Отмена' : 'Удалить',
            type: 'remove',
            disabled: false,
            callback: onRemoveContractHandler,
            belongTo: 'change',
            btnHint: isEdit ? 'Выход из режима "редактирование"' : 'Безвозвратно удалить текущий договор',
            isVisible:
                !dmsState.transferableContractTargetIds?.oldСontractId ||
                dmsState.transferableContractTargetIds.oldСontractId === rest.id
        },
        {
            title: 'Добавить',
            type: 'primary',
            disabled: onSetDisabledMode(),
            callback: onAddContractHandler,
            belongTo: 'add',
            btnHint: 'Добавить новый договор в основной список договоров',
            isVisible:
                !dmsState.transferableContractTargetIds?.oldСontractId ||
                dmsState.transferableContractTargetIds.oldСontractId === rest.id
        },
        {
            title: 'Отмена',
            type: 'remove',
            disabled: false,
            callback: onZeroNewContract,
            belongTo: 'add',
            btnHint: 'Выход их режима добавления договора',
            isVisible:
                !dmsState.transferableContractTargetIds?.oldСontractId ||
                dmsState.transferableContractTargetIds.oldСontractId === rest.id
        }
    ]

    const onGetImportInfoByIdHandler = (key: string) => {
        dmsState.onGetImportInfoById(key, onError)
    }

    return (
        <div
            className={classNames(
                style.container,
                !!dmsState.transferableContractTargetIds.oldСontractId &&
                    rest.id !== dmsState.transferableContractTargetIds.oldСontractId &&
                    style.containerBlured
            )}
        >
            {loader && (
                <DocLoaderModal
                    show
                    fileType={loader}
                    errors={errors}
                    onClose={onCloseLoader}
                    onGetLastErrors={onGetLastErrors}
                    onUploadFileCallback={onUploadFileCallbackHandler}
                    onGetImportInfoByIdHandler={onGetImportInfoByIdHandler}
                />
            )}
            {!!transferableContractError && (
                <TransferNotification
                    transferableContractError={transferableContractError}
                    onAccept={() => {
                        onTransferPoliciesHandler(true)
                    }}
                    onReject={onRejectTransferableContractError}
                />
            )}
            <div className={style.name}>
                <RegularField
                    entity={name}
                    onChange={onChangeName}
                    onSetIsTouched={onSetIsTouchedName}
                    disabled={!isEdit && mode === 'change'}
                />
            </div>
            <div className={style.conclusionAt}>
                <DateField
                    entity={conclusionAt}
                    onChange={onChangeConclusionAt}
                    onSetIsTouched={onSetIsTouchedConclusionAt}
                    disabled={!isEdit && mode === 'change'}
                />
            </div>
            <div className={style.finishedAt}>
                <DateField
                    entity={finishedAt}
                    onChange={onChangeFinishedAt}
                    onSetIsTouched={onSetIsTouchedFinishedAt}
                    disabled={!isEdit && mode === 'change'}
                    titleMarker={
                        rest.completionSoon ? (
                            <div style={{ display: 'flex' }} title='Договор между СК и ЛПУ скоро завершится'>
                                <Vector />
                            </div>
                        ) : undefined
                    }
                />
            </div>
            <div className={style.updateAt}>
                <DateField entity={updateAt} onChange={() => {}} disabled />
            </div>
            <div className={style.actions}>
                {actions
                    .filter((action) => action.belongTo === mode && action.isVisible)
                    .map((action) => {
                        const { title, callback, type, disabled, btnHint } = action
                        return (
                            <button
                                title={btnHint}
                                key={title}
                                className={classNames(style.action, style[type])}
                                onClick={callback}
                                disabled={disabled}
                            >
                                {title}
                            </button>
                        )
                    })}
            </div>
        </div>
    )
})
